import type { PeriodDay, WeekTone } from "@/lib/api/models/week";

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"] as const;

const CELL: Readonly<Record<WeekTone, string>> = {
  good: "bg-ink text-paper",
  careful: "border-[1.5px] border-ink text-ink",
  steady: "text-mid",
};

const SPOKEN: Readonly<Record<WeekTone, string>> = {
  good: ", a best day",
  careful: ", take it slow",
  steady: "",
};

/** "2026-09-25" as a local calendar date, never shifted by the time zone. */
function parseDay(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Monday first, as Indian calendars print it. */
function weekdayIndex(day: Date): number {
  return (day.getDay() + 6) % 7;
}

/**
 * A week or month as a small calendar: best days filled, slow days outlined,
 * the rest plain. Unframed: the caller decides where it sits.
 */
export function DayCalendar({ days }: { readonly days: readonly PeriodDay[] }) {
  if (days.length === 0) return null;
  const first = parseDay(days[0]?.date ?? "");
  const blanks = Array.from({ length: weekdayIndex(first) }, (_, i) => i);
  const has = (tone: WeekTone) => days.some((d) => d.quality === tone);

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-mid" aria-hidden="true">
        {WEEKDAYS.map((w, i) => (
          <span key={i}>{w}</span>
        ))}
      </div>
      <ul className="mt-1.5 grid grid-cols-7 gap-1" aria-label="Your days">
        {blanks.map((i) => (
          <li key={`blank-${i}`} aria-hidden="true" />
        ))}
        {days.map((day, i) => (
          <DayCell key={day.date} day={day} showMonth={i === 0} />
        ))}
      </ul>
      {(has("good") || has("careful")) && (
        <div className="mt-3 flex gap-5 font-mono text-[11px] uppercase tracking-[0.12em] text-mid">
          {has("good") && <Key className="bg-ink" label="Best day" />}
          {has("careful") && <Key className="border-[1.5px] border-ink" label="Take it slow" />}
        </div>
      )}
    </div>
  );
}

function DayCell({ day, showMonth }: { readonly day: PeriodDay; readonly showMonth: boolean }) {
  const date = parseDay(day.date);
  const month = date.toLocaleDateString("en-GB", { month: "short" });
  const spoken = `${date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}${SPOKEN[day.quality]}`;
  return (
    <li
      aria-label={spoken}
      className={`flex h-10 flex-col items-center justify-center font-display text-[16px] leading-none ${CELL[day.quality]}`}
    >
      {date.getDate()}
      {(showMonth || date.getDate() === 1) && <span className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.08em]">{month}</span>}
    </li>
  );
}

function Key({ className, label }: { readonly className: string; readonly label: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className={`inline-block h-3 w-3 ${className}`} aria-hidden="true" />
      {label}
    </span>
  );
}
